// InvoiceWithPdfPreview.jsx
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Input,
  DatePicker,
  Row,
  Col,
  Divider,
  Table,
  InputNumber,
  Card,
  message,
} from 'antd';
import { PlusOutlined, EyeOutlined, DownloadOutlined, MailOutlined } from '@ant-design/icons';
import { PDFViewer, pdf, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import fileDownload from 'js-file-download';

/* ======================= PdfInvoice (your PDF design) ======================= */
const stylesPDF = StyleSheet.create({
  page: { paddingTop: 18, paddingHorizontal: 24, fontSize: 10.5, color: '#2b2b3c', fontFamily: 'Helvetica' },
  hero: { height: 120, backgroundColor: '#ffffff', border: '1px solid #e6e6ef', borderTopLeftRadius: 8, borderTopRightRadius: 8, position: 'relative', overflow: 'hidden' },
  heroOrange: { position: 'absolute', right: 0, top: 0, width: 260, height: 120, backgroundColor: '#25B740', borderBottomLeftRadius: 80 },
  heroTitle: { position: 'absolute', right: 24, top: 34, fontSize: 22, color: '#ffffff', letterSpacing: 1.5 },
  row: { flexDirection: 'row', gap: 18, marginTop: 18 }, col: { flex: 1 },
  hOrange: { color: '#25B740', fontSize: 12, fontWeight: 700, marginBottom: 8 },
  entityName: { fontSize: 11, fontWeight: 700, marginBottom: 4 },
  kv: { flexDirection: 'row', marginBottom: 3 }, kvKey: { width: 86, color: '#7a7a8a' }, kvVal: { flex: 1, color: '#2b2b3c', whiteSpace: 'pre' },
  table: { marginTop: 18, border: '1px solid #d7d7e0', borderRadius: 6, overflow: 'hidden' },
  thead: { flexDirection: 'row', backgroundColor: '#004F7C', color: '#fff', paddingVertical: 8, paddingHorizontal: 10 },
  th: { fontSize: 10, fontWeight: 700 },
  tr: { flexDirection: 'row', paddingVertical: 8, paddingHorizontal: 10, borderTop: '1px solid #ececf3' },
  td: { fontSize: 10 },
  totalsCol: { flex: 1, paddingLeft: 18 },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 },
  totalKey: { color: '#7a7a8a' }, totalVal: { fontWeight: 700 },
  totalFooter: { marginTop: 10, backgroundColor: '#004F7C', color: '#fff', borderRadius: 6, paddingVertical: 10, paddingHorizontal: 12, flexDirection: 'row', justifyContent: 'space-between' },
  totalFooterKey: { fontSize: 12, fontWeight: 700 }, totalFooterVal: { fontSize: 12, fontWeight: 700 },
  bullet: { flexDirection: 'row', marginBottom: 4, marginRight: 8 }, dot: { width: 10, textAlign: 'center' }, bulletText: { flex: 1, lineHeight: 1.4 },
});

const PdfInvoice = ({ data, partnerBankDetails, organisationPartnerDetails }) => {
  // Get currency - now only from 'currency' field
  const currency = data?.currency || 'ALL';
  
  // Use lineItems from the new payload structure
  const items = Array.isArray(data?.lineItems) && data.lineItems.length
    ? data.lineItems.map(item => ({
        description: item.description || '-',
        quantity: item.quantity || 0,
        rate: item.rate || 0,
        LineItemTotal: item.LineItemTotal || 0
      }))
    : [{
        description: 'No items',
        quantity: 0,
        rate: 0,
        LineItemTotal: 0
      }];

  const fmtDate = (d) => {
    if (!d) return '';
    try {
      const x = (typeof d === 'string' || d instanceof String) ? new Date(d) : d;
      return x instanceof Date && !isNaN(x)
        ? x.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        : d;
    } catch { return d; }
  };
  
  const money = (n) => `${currency} ${Number(n || 0).toFixed(2)}`;
  
  // Use the subtotal, taxAmount, and total from the data directly
  const subtotal = typeof data?.subtotal === 'number' ? data.subtotal : items.reduce((s, it) => s + (it.LineItemTotal || 0), 0);
  const taxAmount = typeof data?.taxAmount === 'number' ? data.taxAmount : 0;
  const total = typeof data?.total === 'number' ? data.total : subtotal + taxAmount;
  
  const taxLabel = data?.taxName ? `${data.taxName} (${data.taxPercentage || 0}%)` : 'Tax';

  // Bank details
  let bank = {
    accountHolder: '-',
    bankName: '-',
    accountNumber: '-',
    sortCode: '-',
    iban: '-',
    swift: '-',
  };
  
  if (partnerBankDetails) {
    bank = {
      accountHolder: data?.partner || '-',
      bankName: partnerBankDetails.BankName || '-',
      accountNumber: partnerBankDetails.AccountNumber || '-',
      sortCode: partnerBankDetails.SortCode || '-',
      iban: partnerBankDetails.IBAN || '-',
      swift: partnerBankDetails.SWIFT || '-',
    };
  }

  return (
    <Document>
      <Page size="A4" style={stylesPDF.page} wrap>
        <View style={stylesPDF.hero}>
          <View style={stylesPDF.heroOrange} />
          <Text style={stylesPDF.heroTitle}>INVOICE</Text>
        </View>

        <View style={stylesPDF.row}>
          <View style={stylesPDF.col}>
            <Text style={stylesPDF.hOrange}>Invoice Details</Text>
            <View style={stylesPDF.kv}>
              <Text style={stylesPDF.kvKey}>Invoice Date:</Text>
              <Text style={stylesPDF.kvVal}>{fmtDate(data?.invoiceDate)}</Text>
            </View>
            <View style={stylesPDF.kv}>
              <Text style={stylesPDF.kvKey}>Invoice No:</Text>
              <Text style={stylesPDF.kvVal}>{data?.InvoiceNo || data?.invoiceNumber || '-'}</Text>
            </View>
          </View>
          <View style={stylesPDF.col} />
        </View>

        <View style={stylesPDF.row}>
          <View style={stylesPDF.col}>
            <Text style={stylesPDF.hOrange}>Invoice From</Text>
            {/* Prefer organisationPartnerDetails for Invoice To if available */}
            <Text style={stylesPDF.entityName}>
              {organisationPartnerDetails?.partnerOrganizationName ||
                (typeof data?.partner === 'object' ? (data?.partner?.companyName || '-') : (data?.partner || 'SRIVEN SOLUTIONS LTD'))}
            </Text>
            {(organisationPartnerDetails?.partnerAddress || (typeof data?.partner === 'object' && data?.partner?.address)) && (
              <View style={stylesPDF.kv}>
                <Text style={stylesPDF.kvKey}>Address:</Text>
                <Text style={stylesPDF.kvVal}>{organisationPartnerDetails?.partnerAddress || data?.partner?.address || '-'}</Text>
              </View>
            )}
            {(organisationPartnerDetails?.partnerEmail || (typeof data?.partner === 'object' && data?.partner?.email)) && (
              <View style={stylesPDF.kv}>
                <Text style={stylesPDF.kvKey}>Email:</Text>
                <Text style={stylesPDF.kvVal}>{organisationPartnerDetails?.partnerEmail || data?.partner?.email || '-'}</Text>
              </View>
            )}
            {(organisationPartnerDetails?.partnerContact || (typeof data?.partner === 'object' && data?.partner?.phone)) && (
              <View style={stylesPDF.kv}>
                <Text style={stylesPDF.kvKey}>Phone no:</Text>
                <Text style={stylesPDF.kvVal}>{organisationPartnerDetails?.partnerContact || data?.partner?.phone || '-'}</Text>
              </View>
            )}
          </View>

          <View style={stylesPDF.col}>
            <Text style={stylesPDF.hOrange}>Invoice To</Text>
            {/* Prefer partnerBankDetails for address, email, contact if available */}
            <Text style={stylesPDF.entityName}>{
              partnerBankDetails?.PartnerName ||
              (typeof data?.invoiceFrom === 'object' ? (data?.invoiceFrom?.companyName || '-') : (data?.invoiceFrom || '-'))
            }</Text>
            {(partnerBankDetails?.Address || (typeof data?.invoiceFrom === 'object' && data?.invoiceFrom?.address)) && (
              <View style={stylesPDF.kv}>
                <Text style={stylesPDF.kvKey}>Address:</Text>
                <Text style={stylesPDF.kvVal}>{partnerBankDetails?.Address || data?.invoiceFrom?.address || '-'}</Text>
              </View>
            )}
            {(partnerBankDetails?.Email || (typeof data?.invoiceFrom === 'object' && data?.invoiceFrom?.email)) && (
              <View style={stylesPDF.kv}>
                <Text style={stylesPDF.kvKey}>Email:</Text>
                <Text style={stylesPDF.kvVal}>{partnerBankDetails?.Email || data?.invoiceFrom?.email || '-'}</Text>
              </View>
            )}
            {(partnerBankDetails?.ContactNumber || (typeof data?.invoiceFrom === 'object' && data?.invoiceFrom?.phone)) && (
              <View style={stylesPDF.kv}>
                <Text style={stylesPDF.kvKey}>Phone no:</Text>
                <Text style={stylesPDF.kvVal}>{partnerBankDetails?.ContactNumber || data?.invoiceFrom?.phone || '-'}</Text>
              </View>
            )}
          </View>
        </View>

        <View style={stylesPDF.table}>
          <View style={stylesPDF.thead}>
            <Text style={[stylesPDF.th, { flex: 3 }]}>Description</Text>
            <Text style={[stylesPDF.th, { flex: 0.7 }]}>Quantity</Text>
            <Text style={[stylesPDF.th, { flex: 1.3 }]}>Rate</Text>
            <Text style={[stylesPDF.th, { flex: 1.3 }]}>Total</Text>
          </View>
          {items.map((it, i) => (
            <View key={i} style={stylesPDF.tr}>
              <Text style={[stylesPDF.td, { flex: 3 }]}>{it.description}</Text>
              <Text style={[stylesPDF.td, { flex: 0.7 }]}>{Number(it.quantity)}</Text>
              <Text style={[stylesPDF.td, { flex: 1.3 }]}>{money(it.rate)}</Text>
              <Text style={[stylesPDF.td, { flex: 1.3 }]}>{money(it.LineItemTotal)}</Text> {/* Fixed: changed it.total to it.LineItemTotal */}
            </View>
          ))}
        </View>

        <View style={stylesPDF.row}>
          <View style={stylesPDF.col}>
            <Text style={stylesPDF.hOrange}>Bank Details:</Text>
            <View style={stylesPDF.kv}>
              <Text style={stylesPDF.kvKey}>Account Holder:</Text>
              <Text style={stylesPDF.kvVal}>{bank.accountHolder}</Text>
            </View>
            <View style={stylesPDF.kv}>
              <Text style={stylesPDF.kvKey}>Bank Name:</Text>
              <Text style={stylesPDF.kvVal}>{bank.bankName}</Text>
            </View>
            <View style={stylesPDF.kv}>
              <Text style={stylesPDF.kvKey}>Account Number:</Text>
              <Text style={stylesPDF.kvVal}>{bank.accountNumber}</Text>
            </View>
            <View style={stylesPDF.kv}>
              <Text style={stylesPDF.kvKey}>Sort Code:</Text>
              <Text style={stylesPDF.kvVal}>{bank.sortCode}</Text>
            </View>
            <View style={stylesPDF.kv}>
              <Text style={stylesPDF.kvKey}>IBAN:</Text>
              <Text style={stylesPDF.kvVal}>{bank.iban}</Text>
            </View>
            <View style={stylesPDF.kv}>
              <Text style={stylesPDF.kvKey}>SWIFT:</Text>
              <Text style={stylesPDF.kvVal}>{bank.swift}</Text>
            </View>
          </View>

          <View style={stylesPDF.totalsCol}>
            <View style={stylesPDF.totalRow}>
              <Text style={stylesPDF.totalKey}>Subtotal</Text>
              <Text style={stylesPDF.totalVal}>{money(subtotal)}</Text> {/* Fixed: changed stylePDF.subtotal to stylesPDF.totalVal */}
            </View>
            <View style={stylesPDF.totalRow}>
              <Text style={stylesPDF.totalKey}>{taxLabel}</Text>
              <Text style={stylesPDF.totalVal}>{money(taxAmount)}</Text> {/* Fixed: changed stylePDF.taxAmount to stylesPDF.totalVal */}
            </View>
            <View style={stylesPDF.totalFooter}>
              <Text style={stylesPDF.totalFooterKey}>Total</Text>
              <Text style={stylesPDF.totalFooterVal}>{money(total)}</Text> {/* Fixed: changed stylePDF.total to stylesPDF.totalFooterVal */}
            </View>
          </View>
        </View>

        <View style={{ marginTop: 16 }}>
          <Text style={stylesPDF.hOrange}>Notes / Payment Terms:</Text>
          <Text style={{ fontSize: 10, marginTop: 4 }}>{data?.Notes || data?.notes || '-'}</Text>
        </View>

        <View style={{ marginTop: 16 }}>
          <Text style={stylesPDF.hOrange}>Terms and Conditions:</Text>
          <View style={stylesPDF.bullet}><Text style={stylesPDF.dot}>•</Text><Text style={stylesPDF.bulletText}>Payment will be made by the Customer within 30 days upon receipt of an invoice.</Text></View>
          <View style={stylesPDF.bullet}><Text style={stylesPDF.dot}>•</Text><Text style={stylesPDF.bulletText}>Upon receiving the Services or Deliverable, the customer has a seven-day period to carry out acceptance tests as outlined in the acceptance criteria.</Text></View>
          <View style={stylesPDF.bullet}><Text style={stylesPDF.dot}>•</Text><Text style={stylesPDF.bulletText}>Confidential information shall mean any information disclosed by one party to the other party, including without limitation documents, business plans, and source code.</Text></View>
        </View>
      </Page>
    </Document>
  );
};


// Pure component: expects partnerBankDetails as prop
const InvoiceWithPdfPreview = ({ data, partnerBankDetails, organisationPartnerDetails }) => {
  return <PdfInvoice data={data} partnerBankDetails={partnerBankDetails} organisationPartnerDetails={organisationPartnerDetails} />;
};

export default InvoiceWithPdfPreview;
